import { forwardRef, useEffect, useImperativeHandle, useRef, useState, type SyntheticEvent } from 'react'
import type { Playback, Waveform } from '../api/types'

export interface PlayerHandle {
  /** Jump to a time and play from there. */
  seek: (ms: number) => void
}

interface PlayerProps {
  playback: Playback
  waveform?: Waveform
  /** The playback position in milliseconds, a few times a second and after every seek. */
  onTime?: (ms: number) => void
}

type MediaRef = { current: HTMLMediaElement | null }

/** One player for both kinds of episode: a video element, or audio with a waveform. */
export const Player = forwardRef<PlayerHandle, PlayerProps>(function Player({ playback, waveform, onTime }, ref) {
  const media = useRef<HTMLMediaElement | null>(null)
  useImperativeHandle(
    ref,
    () => ({
      seek: (ms) => {
        const element = media.current
        if (!element) return
        element.currentTime = ms / 1000
        void element.play().catch(() => undefined)
      },
    }),
    [],
  )
  if (!playback.src || !playback.kind) return null
  if (playback.kind === 'video') {
    const report = (event: SyntheticEvent<HTMLVideoElement>) => onTime?.(event.currentTarget.currentTime * 1000)
    return (
      <video
        key={playback.src}
        ref={(element) => {
          media.current = element
        }}
        className="player-video"
        src={playback.src}
        poster={playback.poster ?? undefined}
        controls
        playsInline
        preload="metadata"
        data-testid="player-video"
        onTimeUpdate={report}
        onSeeked={report}
      />
    )
  }
  return <AudioPlayer media={media} src={playback.src} poster={playback.poster} waveform={waveform} onTime={onTime} />
})

function AudioPlayer({
  media,
  src,
  poster,
  waveform,
  onTime,
}: {
  media: MediaRef
  src: string
  poster: string | null
  waveform?: Waveform
  onTime?: (ms: number) => void
}) {
  const [time, setTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const report = (event: SyntheticEvent<HTMLAudioElement>) => {
    const seconds = event.currentTarget.currentTime
    setTime(seconds)
    onTime?.(seconds * 1000)
  }
  return (
    <div className="player-audio">
      {poster && <img className="player-art" src={poster} alt="" />}
      <div className="player-audio-main">
        {waveform && waveform.peaks.length > 0 && (
          <WaveformView
            peaks={waveform.peaks}
            progress={duration > 0 ? time / duration : 0}
            onSeek={(fraction) => {
              const element = media.current
              if (element && duration > 0) element.currentTime = fraction * duration
            }}
          />
        )}
        <audio
          key={src}
          ref={(element) => {
            media.current = element
          }}
          src={src}
          controls
          preload="metadata"
          data-testid="player-audio"
          onTimeUpdate={report}
          onSeeked={report}
          onLoadedMetadata={(event) => setDuration(event.currentTarget.duration)}
        />
      </div>
    </div>
  )
}

export function WaveformView({ peaks, progress, onSeek }: { peaks: number[]; progress: number; onSeek?: (fraction: number) => void }) {
  const canvas = useRef<HTMLCanvasElement>(null)
  const [width, setWidth] = useState(0)

  useEffect(() => {
    const element = canvas.current
    if (!element) return
    // Measure now: a ResizeObserver only reports on the next rendering update,
    // which a background tab may never run, and waiting would leave the first
    // paint blank.
    setWidth(Math.floor(element.getBoundingClientRect().width))
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setWidth(Math.floor(entry.contentRect.width))
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const element = canvas.current
    if (!element || width === 0) return
    const height = 72
    const ratio = window.devicePixelRatio || 1
    element.width = width * ratio
    element.height = height * ratio
    const context = element.getContext('2d')
    if (!context) return
    context.scale(ratio, ratio)
    context.clearRect(0, 0, width, height)
    const styles = getComputedStyle(element)
    const played = styles.getPropertyValue('--accent').trim() || '#d9481b'
    const rest = styles.getPropertyValue('--border-strong').trim() || '#cdc7b9'
    const loudest = Math.max(0.05, ...peaks)
    const bars = Math.max(1, Math.floor(width / 3))
    const step = peaks.length / bars
    for (let i = 0; i < bars; i++) {
      const from = Math.floor(i * step)
      const to = Math.max(from + 1, Math.floor((i + 1) * step))
      let peak = 0
      for (let j = from; j < to && j < peaks.length; j++) peak = Math.max(peak, peaks[j] ?? 0)
      const barHeight = Math.max(2, (peak / loudest) * (height - 4))
      context.fillStyle = i / bars <= progress ? played : rest
      context.fillRect(i * 3, (height - barHeight) / 2, 2, barHeight)
    }
  }, [peaks, progress, width])

  return (
    <canvas
      ref={canvas}
      className="waveform"
      role={onSeek ? 'slider' : 'img'}
      aria-label="Waveform"
      aria-valuemin={onSeek ? 0 : undefined}
      aria-valuemax={onSeek ? 100 : undefined}
      aria-valuenow={onSeek ? Math.round(progress * 100) : undefined}
      onClick={(event) => {
        if (!onSeek) return
        const rect = event.currentTarget.getBoundingClientRect()
        onSeek(Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)))
      }}
    />
  )
}
